import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  alpha,
  Box,
  Divider,
  FormControl,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import { VisitorRegistrationEntry } from "../../features/Booking-Form/typings/booking-inputs";
import { VisitorVisitOnlyLocationAutocomplete } from "./visitor-visit-only-location-autocomplete.component";
import { VisitorVisitTypeRadioCards } from "./visitor-visit-type-radio-cards.component";

export type VisitorRegistrationRowProps = {
  index: number;
  entry: VisitorRegistrationEntry;
  canRemove: boolean;
  emailDuplicate?: boolean;
  /** When true, the visit-type switch is hidden and the row is locked to "visit only". */
  hideBookPlaceSwitch?: boolean;
  /** Conference invite: location comes from the room building; hide the per-visitor picker. */
  hideVisitOnlyLocation?: boolean;
  /** Shown when {@link hideVisitOnlyLocation} is true (building name or pending hint). */
  derivedVisitOnlyLocationLabel?: string | null;
  onPatch: (id: string, patch: Partial<VisitorRegistrationEntry>) => void;
  onRemove: (id: string) => void;
};

export function VisitorRegistrationRow({
  index,
  entry,
  canRemove,
  emailDuplicate = false,
  hideBookPlaceSwitch = false,
  hideVisitOnlyLocation = false,
  derivedVisitOnlyLocationLabel = null,
  onPatch,
  onRemove
}: VisitorRegistrationRowProps) {
  const { t } = useTranslation();
  const n = index + 1;
  const isBookPlace = hideBookPlaceSwitch ? false : entry.bookPlace === true;

  return (
    <Box
      component="section"
      data-testid={`visitor-registration-row-${index}`}
      sx={theme => ({
        bgcolor: "background.paper",
        border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
        borderRadius: 2,
        boxShadow:
          theme.palette.mode === "dark"
            ? `0 2px 16px ${alpha(theme.palette.common.black, 0.35)}`
            : `0 2px 14px ${alpha(theme.palette.common.black, 0.06)}`,
        overflow: "hidden"
      })}
    >
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        sx={theme => ({
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(
            theme.palette.primary.main,
            0.02
          )} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          px: 2,
          py: 1.5
        })}
      >
        <Typography component="h3" sx={{ fontWeight: 600 }} variant="subtitle1">
          {t("_visitorBooking_visitorLabel", { n })}
        </Typography>
        <Tooltip title={<Typography>{t("_visitorBooking_removeVisitor")}</Typography>}>
          <span>
            <IconButton
              aria-label={t("_visitorBooking_removeVisitor")}
              data-testid={`visitor-registration-remove-${index}`}
              disabled={!canRemove}
              edge="end"
              onClick={() => onRemove(entry.id)}
              size="small"
              sx={{ color: "text.secondary" }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Stack spacing={2} sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ width: { xs: "100%", md: "50%" } }}
        >
          <TextField
            required
            fullWidth
            data-testid={`visitor-registration-first-name-${index}`}
            label={t("_visitorBooking_firstName")}
            value={entry.firstName}
            onChange={e => onPatch(entry.id, { firstName: e.target.value })}
          />
          <TextField
            required
            fullWidth
            data-testid={`visitor-registration-surname-${index}`}
            label={t("_visitorBooking_surname")}
            value={entry.surname}
            onChange={e => onPatch(entry.id, { surname: e.target.value })}
          />
        </Stack>
        <TextField
          required
          fullWidth
          type="email"
          autoComplete="email"
          data-testid={`visitor-registration-email-${index}`}
          label={t("_visitorBooking_email")}
          error={emailDuplicate}
          helperText={emailDuplicate ? t("_visitorBooking_duplicateEmailError") : undefined}
          value={entry.email}
          onChange={e => onPatch(entry.id, { email: e.target.value })}
        />
        <TextField
          required
          fullWidth
          multiline
          maxRows={3}
          data-testid={`visitor-registration-visit-reason-${index}`}
          label={t("_visitorBooking_visitReason")}
          placeholder={t("_visitorBooking_visitReasonPlaceholder")}
          value={entry.visitReason ?? ""}
          onChange={e => onPatch(entry.id, { visitReason: e.target.value })}
        />
        <TextField
          fullWidth
          data-testid={`visitor-registration-company-${index}`}
          label={t("_visitorBooking_company")}
          value={entry.company ?? ""}
          onChange={e => onPatch(entry.id, { company: e.target.value })}
        />
        <TextField
          fullWidth
          type="tel"
          data-testid={`visitor-registration-contacts-${index}`}
          label={t("_visitorBooking_contacts")}
          value={entry.contacts ?? ""}
          onChange={e => onPatch(entry.id, { contacts: e.target.value })}
        />

        <Divider sx={theme => ({ borderColor: alpha(theme.palette.divider, 0.12) })} />

        <FormControl component="fieldset" variant="standard" sx={{ m: 0, width: "100%" }}>
          <Grid2 container spacing={2} sx={{ width: "100%" }}>
            {hideVisitOnlyLocation ? (
              <Grid2 size={12}>
                <Typography color="text.secondary" variant="body2">
                  {derivedVisitOnlyLocationLabel?.trim()
                    ? t("_conferenceInvite_visitorLocationDerived", {
                        location: derivedVisitOnlyLocationLabel.trim()
                      })
                    : t("_conferenceInvite_visitorLocationDerivedPending")}
                </Typography>
              </Grid2>
            ) : (
              <Grid2 size={{ xs: 12, sm: hideBookPlaceSwitch ? 12 : 4 }}>
                <VisitorVisitOnlyLocationAutocomplete
                  disabled={isBookPlace}
                  entry={entry}
                  onPatch={onPatch}
                />
              </Grid2>
            )}
            {!hideBookPlaceSwitch && (
              <Grid2 size={{ xs: 12, sm: 8 }} sx={{ alignContent: "center" }}>
                <VisitorVisitTypeRadioCards
                  index={index}
                  isBookPlace={isBookPlace}
                  onBookPlaceChange={bookPlace => {
                    onPatch(entry.id, {
                      bookPlace,
                      visitOnlySelectedLocation: null,
                      visitOnlyLocationName: null,
                      visitOnlyTimezone: "",
                      visitOnlyIsMobileWorking: false
                    });
                  }}
                />
              </Grid2>
            )}
          </Grid2>
        </FormControl>
      </Stack>
    </Box>
  );
}
